import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { take } from 'rxjs/operators';

type ResponseType = 'arraybuffer' | 'blob' | 'json' | 'text';
type RequestParams = { [key: string]: HttpHeaders | HttpParams | ResponseType };

export enum ApiControllers {
  AURORA = 'aurora',
  SVCD_PREALTAS = 'svcd/prealtas'
}

@Injectable({ providedIn: 'root' })
export abstract class GlobalServices {
  protected httpClient = inject(HttpClient);

  private static readonly defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json'
  });

  public static readonly URL_API_HOST: string = environment.api_host;

  getSomething<T>(url: string, params?: HttpParams, responseType?: ResponseType): Observable<T> {
    const requestParams: RequestParams = {};
    if (params) {
      requestParams.params = params;
    }

    if (responseType) {
      requestParams.responseType = responseType;
    }

    if (!requestParams.params && !requestParams.responseType) {
      return this.httpClient.get<T>(url);
    }
    return this.httpClient.get<T>(url, requestParams).pipe(take(1));
  }

  getSomethingWithResponseStatus<T>(url: string): Observable<HttpResponse<T>> {
    return this.httpClient.get<T>(url, { observe: 'response' });
  }

  postSomething<T>(url: string, params, responseType?: ResponseType): Observable<T> {
    const headers = GlobalServices.defaultHeaders;
    const body = JSON.stringify(params);
    const requestParams: RequestParams = { headers };

    if (responseType) {
      requestParams.responseType = responseType;
    }

    return this.httpClient.post<T>(url, body, requestParams).pipe(take(1));
  }

  putSomething<T>(url: string, body, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    const requestHeaders = headers ?? GlobalServices.defaultHeaders;
    const requestBody = JSON.stringify(body);
    return this.httpClient.put<T>(url, requestBody, { headers: requestHeaders, params }).pipe(take(1));
  }

  deleteSomething<T>(url: string, body?, params?): Observable<T> {
    const options: { headers: HttpHeaders; params: HttpParams; body: string } = {
      headers: GlobalServices.defaultHeaders,
      params: null,
      body: null
    };

    if (params) {
      options.params = params;
    }

    if (body) {
      options.body = JSON.stringify(body);
    }

    return this.httpClient.delete<T>(url, options).pipe(take(1));
  }

  prepararBusquedasinPaginacion(formValues: { [s: string]: { key: string; value: string } }): HttpParams {
    let params = new HttpParams();

    params = this.setParamsPrepararBusqueda(formValues, params);

    return params;
  }

  setParamsPrepararBusqueda(formValues: { [s: string]: { key: string; value: string } }, params: HttpParams) {
    if (formValues) {
      for (const [key, value] of Object.entries<{ key: string; value: string }>(formValues)) {
        if (value !== null && value !== undefined && typeof value !== 'object') {
          params = params.set(key, value);
        } else if (value !== null && value !== undefined) {
          if (value instanceof Array) {
            params = params.set(key, value[0]);
          } else {
            params = this.getDateParams(params, key, value);
          }
        }
      }
    }
    return params;
  }

  protected getDateParams(params: HttpParams, key: string, value): HttpParams {
    const { day, month, year } = value;
    if (day && month && year) {
      return params.set(key, `${year}-${month}-${day}`);
    }
    return params;
  }

  protected getHttpParams(params: object): HttpParams {
    let httpParams = new HttpParams();

    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        httpParams = httpParams.set(key, params[key]);
      }
    }
    return httpParams;
  }
}
